import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useQueryClient } from '@tanstack/react-query';

/** Keep the sequence draft reachable while display ownership is being established. */
export default function SequenceUsageResume() {
  const { user } = useAuth();
  const userId = user?.id;
  const location = useLocation();
  const queryClient = useQueryClient();
  const [sequenceId, setSequenceId] = useState<string | null>(null);
  useEffect(() => {
    const read = () => {
      try {
        const value = userId ? sessionStorage.getItem(`sequence-usage-resume:${userId}`) : null;
        setSequenceId(value && /^[0-9a-f-]{36}$/i.test(value) ? value : null);
      } catch { setSequenceId(null); }
    };
    read();
    const changed = (event: Event) => {
      read();
      if (!(event instanceof CustomEvent && event.detail?.queriesInvalidated)) {
        void queryClient.invalidateQueries({ predicate: ({ queryKey }) =>
          ['sequenceUsage', 'sequenceDisplays', 'displaySeasons', 'creatorStats'].includes(String(queryKey[0])),
        });
      }
    };
    window.addEventListener('sequence-usage-changed', changed);
    return () => window.removeEventListener('sequence-usage-changed', changed);
  }, [userId, location.key, queryClient]);
  if (!sequenceId || location.pathname.startsWith('/sequence/')) return null;
  return <aside aria-label="Saved sequence usage draft" className="border-b bg-card px-4 py-3 text-card-foreground">
    <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-2">
      <p className="text-sm">Your sequence usage draft is saved. Return after creating or claiming your display.</p>
      <div className="flex gap-2">
        <Button asChild size="sm" variant="outline"><Link to={`/sequence/${sequenceId}?resumeSequenceUsage=${sequenceId}`}>Return to sequence</Link></Button>
        <Button size="sm" variant="ghost" onClick={() => {
          try { if (user) sessionStorage.removeItem(`sequence-usage-resume:${user.id}`); } catch { /* Dismissal still works when browser storage is blocked. */ }
          setSequenceId(null);
        }}>Dismiss</Button>
      </div>
    </div>
  </aside>;
}
